import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const MediaScanner = () => {
    const [scanResults, setScanResults] = useState([]);
    const [cameraId, setCameraId] = useState(null);
    const [scanner, setScanner] = useState(null);
    const [showTick, setShowTick] = useState(false);
    const [showCross, setShowCross] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [stats, setStats] = useState({ total: 0, present: 0 });
    const [hasCamera, setHasCamera] = useState(true);

    const isProcessingRef = useRef(false);
    const processedCodesRef = useRef(new Set());
    const scanTimeoutRef = useRef(null);

    const ERROR_MESSAGES = {
        INVALID_QR: "Invalid QR code format.",
        USER_NOT_FOUND: "Media user not found.",
        NOT_APPROVED: "Media user not approved for attendance.",
        ALREADY_MARKED: "Attendance already marked for today.",
        NETWORK_ERROR: "Network error. Please check your connection.",
        SERVER_ERROR: "Server error. Please try again.",
        NO_CAMERA: "Camera not available.",
    };

    const SCAN_COOLDOWN = 2000;

    useEffect(() => {
        initializeCamera();
        return cleanupScanner;
    }, []);

    useEffect(() => {
        if (cameraId && hasCamera) {
            startScanner();
        }
    }, [cameraId, hasCamera]);

    const initializeCamera = async () => {
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0) {
                const backCamera = devices.find(device =>
                    device.label.toLowerCase().includes("back")
                );
                setCameraId(backCamera ? backCamera.id : devices[0].id);
                setHasCamera(true);
            } else {
                setHasCamera(false);
                showError(ERROR_MESSAGES.NO_CAMERA);
            }
        } catch (err) {
            console.error("Error fetching cameras:", err);
            setHasCamera(false);
            showError(ERROR_MESSAGES.NO_CAMERA);
        }
    };

    const startScanner = async () => {
        if (!cameraId || !hasCamera) return;

        const html5QrCode = new Html5Qrcode("media-qr-reader", {
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        });

        try {
            await html5QrCode.start(
                cameraId,
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0,
                },
                handleScanSuccess,
                () => { }
            );
            setScanner(html5QrCode);
        } catch (err) {
            console.error("Error starting scanner:", err);
            setHasCamera(false);
            showError(ERROR_MESSAGES.NO_CAMERA);
        }
    };

    const cleanupScanner = () => {
        if (scanner && scanner.isScanning) {
            scanner.stop().catch(err =>
                console.error("Error stopping scanner:", err)
            );
        }
        if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
        }
    };

    const handleScanSuccess = async (decodedText) => {
        if (isProcessingRef.current) return;

        const userId = decodedText.trim();

        if (!userId || userId.length !== 24) {
            showError(ERROR_MESSAGES.INVALID_QR);
            return;
        }

        // Get today's date in YYYY-MM-DD format
        const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
        const todayKey = `${userId}-${today}`;

        // Check if already scanned today
        if (processedCodesRef.current.has(todayKey)) {
            showError(ERROR_MESSAGES.ALREADY_MARKED);
            return;
        }

        isProcessingRef.current = true;
        setIsProcessing(true);

        try {
            const response = await markAttendance(userId);

            if (response.success) {
                handleSuccessfulScan(response.user, userId, todayKey);
            } else {
                handleScanError(response.error, userId, todayKey);
            }
        } catch (err) {
            console.error("Scan error:", err);
            showError(ERROR_MESSAGES.NETWORK_ERROR);
        } finally {
            setTimeout(() => {
                isProcessingRef.current = false;
                setIsProcessing(false);
            }, 800);
        }
    };

    const markAttendance = async (userId) => {
        try {
            const response = await fetch("/api/media-users/mark-attendance", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    userId: userId,
                    scannedAt: new Date().toISOString(),
                    scannerInfo: "Media Scanner App"
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                return { success: false, error: errorData.error };
            }

            const result = await response.json();
            return { success: true, user: result.user };
        } catch (error) {
            return { success: false, error: ERROR_MESSAGES.NETWORK_ERROR };
        }
    };

    const handleSuccessfulScan = (userData, userId, todayKey) => {
        processedCodesRef.current.add(todayKey);

        const scanResult = {
            name: userData.firstName,
            designation: userData.designation,
            mediaHouse: userData.mediaHouseName,
            timestamp: new Date().toISOString(),
            status: "Present"
        };

        setScanResults(prev => [scanResult, ...prev.slice(0, 9)]);
        updateStats();

        showSuccess(`✓ ${userData.firstName} marked present`);

        scanTimeoutRef.current = setTimeout(() => {
            processedCodesRef.current.delete(todayKey);
        }, SCAN_COOLDOWN);
    };

    const handleScanError = (error, userId, todayKey) => {
        let errorMsg = ERROR_MESSAGES.SERVER_ERROR;

        if (error.includes("not found")) {
            errorMsg = ERROR_MESSAGES.USER_NOT_FOUND;
        } else if (error.includes("not approved")) {
            errorMsg = ERROR_MESSAGES.NOT_APPROVED;
        } else if (error.includes("already marked")) {
            errorMsg = ERROR_MESSAGES.ALREADY_MARKED;
            processedCodesRef.current.add(todayKey);
        } else {
            errorMsg = error;
        }

        showError(errorMsg);
    };

    const updateStats = () => {
        setStats(prev => ({
            total: prev.total + 1,
            present: prev.present + 1
        }));
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowTick(true);
        setErrorMessage(null);
        setShowCross(false);

        setTimeout(() => {
            setShowTick(false);
            setSuccessMessage(null);
        }, 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowCross(true);
        setSuccessMessage(null);
        setShowTick(false);

        setTimeout(() => {
            setShowCross(false);
            setErrorMessage(null);
        }, 3000);
    };

    const clearScans = () => {
        setScanResults([]);
        setStats({ total: 0, present: 0 });
        processedCodesRef.current.clear();
    };

    return (
        <div style={styles.container}>
            {/* Header Section */}
            <div style={styles.header}>
                <img src="/khasdar.jpg" alt="Logo" style={styles.logo} />
                <div style={styles.titleContainer}>
                    <h1 style={styles.title}>Media Scanner</h1>
                    <div style={styles.dateInfo}>
                        {new Date().toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div style={styles.statsContainer}>
                <div style={styles.statItem}>
                    <div style={styles.statNumber}>{stats.total}</div>
                    <div style={styles.statLabel}>Today's Scans</div>
                </div>
                <div style={styles.statItem}>
                    <div style={styles.statNumber}>{stats.present}</div>
                    <div style={styles.statLabel}>Present</div>
                </div>
            </div>

            {/* Scanner Section */}
            <div style={styles.scannerSection}>
                {hasCamera ? (
                    <div id="media-qr-reader" style={styles.qrReader}></div>
                ) : (
                    <div style={styles.cameraPlaceholder}>
                        <div style={styles.placeholderIcon}>📷</div>
                        <div style={styles.placeholderText}>
                            Camera not available
                        </div>
                    </div>
                )}

                <div style={styles.buttonGroup}>
                    <button onClick={clearScans} style={styles.clearButton}>
                        🗑️ Clear Today's History
                    </button>
                </div>

                {isProcessing && (
                    <div style={styles.processing}>
                        <div style={styles.spinner}></div>
                        Processing...
                    </div>
                )}

                {showTick && <div style={styles.tick}>✅</div>}
                {showCross && <div style={styles.cross}>❌</div>}

                {successMessage && (
                    <div style={styles.successMessage}>
                        <div style={styles.successTitle}>Success!</div>
                        {successMessage}
                    </div>
                )}

                {errorMessage && (
                    <div style={styles.errorMessage}>
                        <div style={styles.errorTitle}>Error</div>
                        {errorMessage}
                    </div>
                )}
            </div>

            {/* Recent Scans */}
            {scanResults.length > 0 && (
                <div style={styles.resultsContainer}>
                    <div style={styles.resultsHeader}>
                        <h3 style={styles.historyTitle}>
                            Today's Scans ({scanResults.length})
                        </h3>
                    </div>
                    <div style={styles.resultsList}>
                        {scanResults.map((result, index) => (
                            <div key={index} style={styles.resultItem}>
                                <div style={styles.userInfo}>
                                    <div style={styles.userName}>{result.name}</div>
                                    <div style={styles.userDetails}>
                                        {result.designation} • {result.mediaHouse}
                                    </div>
                                </div>
                                <div style={styles.scanTime}>
                                    {new Date(result.timestamp).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes popIn {
                    0% { transform: scale(0.5); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

const styles = {
    container: {
        textAlign: "center",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: "20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        minHeight: "100vh",
        boxSizing: "border-box",
    },
    header: {
        marginBottom: "25px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    },
    logo: {
        width: "80px",
        height: "80px",
        marginBottom: "15px",
        objectFit: "contain",
        borderRadius: "10px",
    },
    titleContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
    },
    title: {
        fontSize: "clamp(28px, 5vw, 40px)",
        fontWeight: "bold",
        margin: "0 0 10px 0",
        textAlign: "center",
        textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
        width: "100%",
    },
    dateInfo: {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        padding: "8px 20px",
        borderRadius: "10px",
        backdropFilter: "blur(10px)",
        fontSize: "14px",
        fontWeight: "500",
    },
    statsContainer: {
        display: "flex",
        justifyContent: "center",
        gap: "20px",
        marginBottom: "25px",
        flexWrap: "wrap",
    },
    statItem: {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        padding: "15px 20px",
        borderRadius: "15px",
        backdropFilter: "blur(10px)",
        minWidth: "100px",
        flex: "1",
        maxWidth: "150px",
    },
    statNumber: {
        fontSize: "clamp(24px, 4vw, 32px)",
        fontWeight: "bold",
        marginBottom: "5px",
    },
    statLabel: {
        fontSize: "14px",
        opacity: 0.9,
    },
    scannerSection: {
        position: "relative",
        maxWidth: "400px",
        margin: "0 auto 30px",
        width: "100%",
    },
    qrReader: {
        width: "100%",
        border: "4px solid #fff",
        borderRadius: "15px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        overflow: "hidden",
        minHeight: "300px",
    },
    cameraPlaceholder: {
        width: "100%",
        height: "300px",
        border: "4px dashed rgba(255,255,255,0.5)",
        borderRadius: "15px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
    },
    placeholderIcon: {
        fontSize: "48px",
        marginBottom: "15px",
        opacity: 0.7,
    },
    placeholderText: {
        fontSize: "18px",
        fontWeight: "bold",
    },
    buttonGroup: {
        display: "flex",
        gap: "10px",
        justifyContent: "center",
        marginTop: "15px",
        flexWrap: "wrap",
    },
    clearButton: {
        padding: "12px 16px",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        color: "#fff",
        border: "2px solid #fff",
        borderRadius: "25px",
        cursor: "pointer",
        fontSize: "14px",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s ease",
        flex: "1",
        minWidth: "200px",
    },
    processing: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontSize: "18px",
        fontWeight: "bold",
        margin: "20px 0",
        color: "#fff",
    },
    spinner: {
        width: "20px",
        height: "20px",
        border: "3px solid rgba(255,255,255,0.3)",
        borderTop: "3px solid #fff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    },
    tick: {
        fontSize: "clamp(60px, 10vw, 80px)",
        margin: "20px 0",
        animation: "popIn 0.5s ease",
    },
    cross: {
        fontSize: "clamp(60px, 10vw, 80px)",
        margin: "20px 0",
        animation: "popIn 0.5s ease",
    },
    successMessage: {
        backgroundColor: "rgba(40, 167, 69, 0.9)",
        color: "#fff",
        fontSize: "clamp(16px, 3vw, 18px)",
        padding: "15px",
        margin: "15px 0",
        borderRadius: "12px",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
    },
    errorMessage: {
        backgroundColor: "rgba(220, 53, 69, 0.9)",
        color: "#fff",
        fontSize: "clamp(14px, 3vw, 16px)",
        padding: "15px",
        margin: "15px 0",
        borderRadius: "12px",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
    },
    successTitle: {
        fontSize: "14px",
        fontWeight: "bold",
        marginBottom: "5px",
        opacity: 0.9,
    },
    errorTitle: {
        fontSize: "14px",
        fontWeight: "bold",
        marginBottom: "5px",
        opacity: 0.9,
    },
    resultsContainer: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        padding: "20px",
        borderRadius: "15px",
        maxWidth: "500px",
        margin: "0 auto",
        backdropFilter: "blur(10px)",
        width: "100%",
        boxSizing: "border-box",
    },
    historyTitle: {
        fontSize: "18px",
        fontWeight: "bold",
        margin: "0 0 15px 0",
        textAlign: "center",
    },
    resultsList: {
        maxHeight: "300px",
        overflowY: "auto",
    },
    resultItem: {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        padding: "12px",
        marginBottom: "8px",
        borderRadius: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
    },
    userInfo: {
        textAlign: "left",
        flex: "1",
        minWidth: "200px",
    },
    userName: {
        fontSize: "16px",
        fontWeight: "bold",
        marginBottom: "4px",
    },
    userDetails: {
        fontSize: "14px",
        opacity: "0.8",
    },
    scanTime: {
        fontSize: "14px",
        opacity: 0.7,
        whiteSpace: "nowrap",
    },
};

export default MediaScanner;